import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Contexts
import { AuthProvider } from '@/contexts/AuthContext';
import { BusinessProvider } from '@/contexts/BusinessContext';
import { PartnerAuthProvider } from '@/contexts/PartnerAuthContext';

// Components
import { Layout } from '@/components/Layout';
import { ProtectedRoute, AdminRoute } from '@/components/ProtectedRoute';
import { PartnerProtectedRoute } from '@/components/partner/PartnerProtectedRoute';
import { PartnerLayout } from '@/components/partner/PartnerLayout';

// Pages - Public
import { LoginPage } from '@/pages/LoginPage';

// Pages - Rep
import { DashboardPage } from '@/pages/DashboardPage';
import { CompaniesPage } from '@/pages/CompaniesPage';
import { CompanyDetailPage } from '@/pages/CompanyDetailPage';
import { CompanyFormPage } from '@/pages/CompanyFormPage';
import { BusinessCardImportPage } from '@/pages/BusinessCardImportPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { ReportsPage } from '@/pages/ReportsPage';

// Pages - Admin
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { CommissionRulesPage } from '@/pages/admin/CommissionRulesPage';
import { AdminReportsPage } from '@/pages/admin/AdminReportsPage';
import { EvidenceLibraryPage } from '@/pages/admin/EvidenceLibraryPage';
import { PartnerManagementPage } from '@/pages/admin/PartnerManagementPage';
import { DiagnosticsPage } from '@/pages/admin/DiagnosticsPage';
import { AllProjectsPage } from '@/pages/admin/AllProjectsPage';
import { BulkImportPage } from '@/pages/admin/BulkImportPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { MergeCompaniesPage } from '@/pages/admin/MergeCompaniesPage';

// Pages - Partner Portal
import { PartnerLoginPage } from '@/pages/partner/PartnerLoginPage';
import { PartnerDashboardPage } from '@/pages/partner/PartnerDashboardPage';
import { PartnerProjectDetailPage } from '@/pages/partner/PartnerProjectDetailPage';

// ===========================================
// React Query Client
// ===========================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ===========================================
// App Component
// ===========================================

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <BusinessProvider>
            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#333',
                  color: '#fff',
                },
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />

            {/* Routes */}
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes with layout */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                {/* Rep routes */}
                <Route index element={<DashboardPage />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="companies/new" element={<CompanyFormPage />} />
                <Route path="companies/:id" element={<CompanyDetailPage />} />
                <Route path="companies/:id/edit" element={<CompanyFormPage />} />
                <Route path="import/business-cards" element={<BusinessCardImportPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="reports" element={<ReportsPage />} />

                {/* Admin routes */}
                <Route
                  path="admin"
                  element={
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <AdminRoute>
                      <UsersPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/rules"
                  element={
                    <AdminRoute>
                      <CommissionRulesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/reports"
                  element={
                    <AdminRoute>
                      <AdminReportsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/evidence"
                  element={
                    <AdminRoute>
                      <EvidenceLibraryPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/partners"
                  element={
                    <AdminRoute>
                      <PartnerManagementPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/diagnostics"
                  element={
                    <AdminRoute>
                      <DiagnosticsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/projects"
                  element={
                    <AdminRoute>
                      <AllProjectsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/import"
                  element={
                    <AdminRoute>
                      <BulkImportPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/settings"
                  element={
                    <AdminRoute>
                      <SettingsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/merge-companies"
                  element={
                    <AdminRoute>
                      <MergeCompaniesPage />
                    </AdminRoute>
                  }
                />
              </Route>

              {/* Partner Portal routes (separate auth context) */}
              <Route
                path="/partner/*"
                element={
                  <PartnerAuthProvider>
                    <Routes>
                      {/* Partner login (public) */}
                      <Route path="login" element={<PartnerLoginPage />} />

                      {/* Partner protected routes with layout */}
                      <Route
                        element={
                          <PartnerProtectedRoute>
                            <PartnerLayout />
                          </PartnerProtectedRoute>
                        }
                      >
                        <Route index element={<PartnerDashboardPage />} />
                        <Route path="projects/:projectId" element={<PartnerProjectDetailPage />} />
                      </Route>
                    </Routes>
                  </PartnerAuthProvider>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BusinessProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
